/**
 * Client del connettore MCP.
 * Spec: docs/ai-program/10-byok-and-mcp-rework.md (Fase 3).
 *
 * MCP serve il caso opposto all'assistente in-app: invece di portare l'AI
 * dentro l'applicazione, porta i dati dell'applicazione dentro il Claude o il
 * ChatGPT che il coach usa gia'. L'inferenza la paga il suo abbonamento.
 */
import { fetchAPI } from "./utils";

/** Chiave sotto cui si parcheggia il consenso quando serve prima il login. */
const PENDING_KEY = "kraveon:mcp-pending-authorize";

export function getMcpConnections() {
	return fetchAPI("/mcp/connections");
}

export function disconnectMcpClient(clientId) {
	return fetchAPI(`/mcp/connections/${clientId}`, { method: "DELETE" });
}

export function getPendingAuthorization(requestId) {
	return fetchAPI(`/mcp/oauth/pending/${requestId}`);
}

/**
 * `extraScopes`: i permessi che il client non aveva chiesto e che il coach ha
 * lasciato spuntati. I client chiedono la lista che conoscevano quando si sono
 * registrati: un permesso nato dopo (come `models:write`) arriva solo cosi'.
 */
export function decideAuthorization(requestId, approve, extraScopes = []) {
	return fetchAPI("/mcp/oauth/decision", {
		method: "POST",
		body: JSON.stringify({ request_id: requestId, approve, extra_scopes: extraScopes }),
	});
}

/**
 * Il consenso arriva da un redirect del client MCP, quindi puo' cogliere il
 * coach non autenticato. Senza parcheggiare la destinazione, il login lo
 * porterebbe in dashboard e la richiesta andrebbe persa.
 */
export function rememberPendingAuthorization() {
	try {
		sessionStorage.setItem(
			PENDING_KEY,
			window.location.pathname + window.location.search,
		);
	} catch {
		// Senza sessionStorage si perde solo la comodita' del ritorno.
	}
}

export function consumePendingAuthorization() {
	try {
		const target = sessionStorage.getItem(PENDING_KEY);
		if (target) sessionStorage.removeItem(PENDING_KEY);
		return target;
	} catch {
		return null;
	}
}

// Le etichette descrivono il **permesso**, non il tool: il coach sta per
// concedere qualcosa a un programma di terzi, e deve capire cosa concede senza
// sapere come si chiamano i nostri strumenti.
const SCOPE_LABELS = {
	// La lettura include il calendario gare: lo diceva già il tool
	// `get_athlete_agenda` prima che ne esistesse uno dedicato, e un'etichetta
	// che tace su un dato che passa è la stessa svista del diario benessere.
	"programs:read":
		"Leggere atleti, risultati, esercizi, programmi, calendario gare e modelli prestativi",
	// Dal 2026-10-23 questo permesso **corregge** anche ciò che ha scritto
	// (note, carichi, reps di esercizi già presenti), e l'etichetta lo dice:
	// un consenso che si ferma a «aggiungere» tace su una scrittura che
	// avviene, ed è la stessa svista dell'etichetta che non nominava il
	// diario benessere. Correggere in posto resta lontano dal cancellare, che
	// ha il suo permesso qui sotto.
	"programs:write":
		"Creare programmi, aggiungere settimane, giorni ed esercizi, e correggere quelli già scritti",
	// Terzo permesso, separato dagli altri due perché è l'unico che toglie
	// roba: concederlo insieme alla scrittura avrebbe significato che chiunque
	// avesse già autorizzato un client a scrivere si sarebbe trovato, senza
	// averlo chiesto, un collegamento capace di cancellare.
	"programs:modify": "Cancellare esercizi, giorni e settimane dai programmi",
	// Quarto permesso: `programs:write` scrive nella libreria del coach,
	// questo nel calendario dei suoi atleti. Sono due consensi diversi.
	"competitions:write":
		"Aggiungere e modificare le gare nel calendario degli atleti",
	// Quinto permesso (2026-10-27): i modelli prestativi, con cui sono valutati
	// gli atleti. Uno solo anche per togliere e pubblicare, quindi l'etichetta
	// lo dice: un consenso che tace su una cancellazione possibile non è
	// informato.
	"models:write":
		"Configurare i tuoi modelli prestativi (domini, scale, tabelle per categoria), anche toglierne parti, pubblicarli o cancellarli",
};

/** I permessi che tolgono qualcosa, non che aggiungono. */
export const DESTRUCTIVE_SCOPES = ["programs:modify", "models:write"];

export function isDestructiveScope(scope) {
	return DESTRUCTIVE_SCOPES.includes(scope);
}

export function describeScope(scope) {
	return SCOPE_LABELS[scope] || scope;
}
